import { api } from '@goaltree/backend/convex/_generated/api'
import { CANOPY_BY_ID } from '@goaltree/backend/convex/lib/progression'
import { cn } from '@goaltree/ui/lib/utils'
import type { ConvexReactClient } from 'convex/react'
import { useConvex } from 'convex/react'
import { type ReactNode, useEffect, useSyncExternalStore } from 'react'

// Every avatar on screen asks for its user's canopy; requests are pooled into
// one getCanopies subscription so a leaderboard page is a single query, not 50.
const MAX_IDS = 100
const requested = new Set<string>()
let canopies: Record<string, string> = {}
const listeners = new Set<() => void>()
let unsubscribe: (() => void) | null = null
let timer: ReturnType<typeof setTimeout> | undefined

function emit(next: Record<string, string> | undefined) {
  if (!next) return
  canopies = next
  listeners.forEach((l) => l())
}

function resubscribe(client: ConvexReactClient) {
  clearTimeout(timer)
  timer = setTimeout(() => {
    unsubscribe?.()
    const watch = client.watchQuery(api.progression.getCanopies, {
      userIds: [...requested].slice(-MAX_IDS),
    })
    const read = () => {
      try {
        emit(watch.localQueryResult())
      } catch {}
    }
    unsubscribe = watch.onUpdate(read)
    read()
  }, 30)
}

function subscribe(l: () => void) {
  listeners.add(l)
  return () => listeners.delete(l)
}

export function useCanopy(userId: string | null | undefined): string | null {
  const client = useConvex()
  useEffect(() => {
    if (!userId || requested.has(userId)) return
    requested.add(userId)
    resubscribe(client)
  }, [userId, client])
  return useSyncExternalStore(
    subscribe,
    () => (userId ? (canopies[userId] ?? null) : null),
    () => null,
  )
}

const LEAVES = 16
const LEAF = 'M0,-7 C4,-3 4,3 0,7 C-4,3 -4,-3 0,-7Z'

// Leaf ring drawn just outside a round avatar. Overflows its box, so swapping
// it in never shifts layout.
export function CanopyRing({ canopy, className }: { canopy: string; className?: string }) {
  const def = CANOPY_BY_ID[canopy]
  if (!def) return null
  return (
    <svg
      aria-hidden
      viewBox="-70 -70 140 140"
      className={cn(
        // Important: menu buttons force child svgs to icon size ([&_svg]:size-4).
        'pointer-events-none absolute -inset-[20%] !size-[140%] !max-w-none motion-safe:animate-spin motion-safe:[animation-duration:90s]',
        className,
      )}
    >
      <circle r="52" fill="none" stroke={def.colors[0]} strokeOpacity={0.45} strokeWidth={2} />
      {Array.from({ length: LEAVES }, (_, i) => (
        <path
          key={i}
          d={LEAF}
          fill={def.colors[i % 3]}
          transform={`rotate(${(360 / LEAVES) * i}) translate(0,-57) rotate(${i % 2 ? 32 : -32})`}
          className={cn(i % 4 === 0 && 'motion-safe:animate-pulse')}
          style={i % 4 === 0 ? { animationDelay: `${(i / LEAVES) * 2}s` } : undefined}
        />
      ))}
    </svg>
  )
}

// Wraps any round avatar. Pass `canopy` directly, or `userId` to look it up.
export function CanopyAvatar({
  userId,
  canopy,
  className,
  children,
}: {
  userId?: string | null
  canopy?: string | null
  className?: string
  children: ReactNode
}) {
  const looked = useCanopy(canopy === undefined ? userId : null)
  const id = canopy === undefined ? looked : canopy
  return (
    <span className={cn('relative inline-flex shrink-0', className)}>
      {children}
      {id && <CanopyRing canopy={id} />}
    </span>
  )
}
